import { useState, useEffect, useCallback } from 'react';
import {
  fetchSimpleDocumentTemplates,
  uploadSimpleDocumentTemplate,
  downloadSimpleDocumentTemplate,
  deleteSimpleDocumentTemplate,
  updateSimpleDocumentTemplate,
  triggerFileDownload,
  type SimpleDocumentTemplate,
} from '../api/documents';

export function useDocumentTemplates() {
  const [documents, setDocuments] = useState<SimpleDocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Charger les documents
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSimpleDocumentTemplates();
      console.log('[DEBUG] Documents chargés depuis l\'API:', data.length, 'fichiers');
      console.log('[DEBUG] Premiers documents:', data.slice(0, 3));
      setDocuments(data);
    } catch (err: any) {
      console.error('Erreur chargement documents:', err);
      setError(err?.response?.data?.error || err?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  // Téléverser un document
  const uploadDocument = async (file: File, customName?: string) => {
    try {
      setUploading(true);
      setError(null);
      const newDocument = await uploadSimpleDocumentTemplate(file, customName);
      setDocuments(prev => [newDocument, ...prev]);
      return { success: true, document: newDocument };
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Erreur lors du téléversement';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setUploading(false);
    }
  };

  // Télécharger un document
  const downloadDocument = async (document: SimpleDocumentTemplate) => {
    try {
      // Utiliser le chemin relatif si disponible
      const filename = document.relative_path || document.fileName;
      const blob = await downloadSimpleDocumentTemplate(filename);
      triggerFileDownload(blob, document.fileName);
      return { success: true };
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Erreur lors du téléchargement';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Supprimer un document
  const deleteDocument = async (filename: string) => {
    try {
      await deleteSimpleDocumentTemplate(filename);
      // Filtrer en utilisant aussi relative_path ou fileName
      setDocuments(prev => prev.filter(doc => 
        doc.fileName !== filename && 
        (doc.relative_path || doc.fileName) !== filename
      ));
      return { success: true };
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Erreur lors de la suppression';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Mettre à jour un document (renommer et/ou remplacer)
  const updateDocument = async (oldFilename: string, newName?: string, newFile?: File) => {
    try {
      setUploading(true);
      setError(null);
      const updatedDocument = await updateSimpleDocumentTemplate(oldFilename, newName, newFile);
      // Comparer avec fileName ou relative_path
      setDocuments(prev => prev.map(doc => {
        const docIdentifier = doc.relative_path || doc.fileName;
        return docIdentifier === oldFilename ? updatedDocument : doc;
      }));
      return { success: true, document: updatedDocument };
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Erreur lors de la mise à jour';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setUploading(false);
    }
  };

  // Charger les documents au montage
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return {
    documents,
    loading,
    error,
    uploading,
    uploadDocument,
    downloadDocument,
    deleteDocument,
    updateDocument,
    refetch: loadDocuments,
  };
}
